import React, { FC, SetStateAction, Dispatch, useCallback } from 'react';

import { Tooltip } from 'react-tooltip';

import { ReactComponent as EditSvg } from 'assets/svg/pencil.svg';
import { ReactComponent as DeleteSvg } from 'assets/svg/trash-can.svg';
import { DAXIA_PATTERN, INVESTMENTS_DAXIA_PRICE_PER_SHARE_TOOLTIP_CONTENT } from 'constants/investments';
import useModal from 'hooks/use-modal/useModal';
import {
  CompanyActiveShareExchange,
  CompanyWithUserShareExchangeStatus,
  HandleClickRequestShareExchangeType,
  Investment,
  InvestmentStatuses,
  investmentStatusesColoredLabel,
  InvestmentWithCompany,
} from 'interfaces';
import { ButtonsActions, ColoredLabel } from 'shared-components';
import { TableData, TableRow } from 'shared-components/table';
import TableDataWithTooltip from 'shared-components/table/TableDataWithTooltip';
import { formatAsThousands, formatNumberInputValue } from 'utils/shared';

import InvestmentsTableFormRow from '../investments-table/InvestmentsTableFormRow';
import MyInvestmentExchangeButton from './MyInvestmentsExchangeButton';
import MyInvestmentsNameTableDataContent from './MyInvestmentsNameTableDataContent';

type Props = {
  isEditing?: boolean;
  isFirstElement: boolean;
  companyName?: string;
  companyId: number;
  companyNumber: string;
  setEditId?: Dispatch<SetStateAction<string | null>>;
  disabledActions: boolean;
  companyActiveShareExchange: CompanyActiveShareExchange | null;
  setDeletingInvestment?: (investment: InvestmentWithCompany | null) => void;
  handleClickRequestShareExchange?: HandleClickRequestShareExchangeType;
  investment: InvestmentWithCompany;
  onSuccessEditInvestment?: VoidFunction;
  declineReason: string;
  isActiveExchange: boolean;
  shareExchangeRequestId: number;
  serDeclined: boolean;
  draftDealId?: number;
} & CompanyWithUserShareExchangeStatus;

const MyCompaniesWithInvestmentsTableItem: FC<Props> = ({
  isEditing,
  isFirstElement,
  companyName,
  companyNumber,
  companyId,
  setEditId,
  disabledActions,
  companyActiveShareExchange,
  setDeletingInvestment,
  handleClickRequestShareExchange,
  investment,
  onSuccessEditInvestment,
  declineReason,
  isActiveExchange,
  shareExchangeRequestId,
  serDeclined,
  userCompanySEStatus,
  draftDealId,
}) => {
  const {
    status,
    investmentName,
    originalInvestmentPrice,
    totalShares,
    companyNumber: investmentCompanyNumber,
    isBlended,
    currentInvestmentValue,
    ...investmentData
  } = investment;

  const isDaxiaInvestment = investmentName?.includes(DAXIA_PATTERN);
  const tooltipAnchorId = `${investment.id}`;

  const { isOpen, onOpen, onClose } = useModal();

  const getInvestmentButtonsActions = () => [
    {
      id: 'edit-investment',
      icon: EditSvg,
      action: () => {
        setEditId?.(investment.id);
      },
      disabled: disabledActions,
    },
    {
      id: 'delete-investment',
      icon: DeleteSvg,
      action: () => setDeletingInvestment?.({ ...investment, companyName }),
      disabled: disabledActions,
    },
  ];

  const investmentToEdit = { ...investmentData, companyName, companyNumber } as Investment;

  const onClickRequest = useCallback(() => {
    handleClickRequestShareExchange?.({
      companyId,
      userCompanySEStatus,
      shareExchangeRequestId,
      serDeclined,
      draftDealId,
    });
  }, [handleClickRequestShareExchange, companyId, userCompanySEStatus, shareExchangeRequestId, serDeclined]);

  return (
    <>
      <TableRow>
        {setEditId && (
          <TableData className='pl-2 min-w-150'>
            {isFirstElement && !isDaxiaInvestment && (
              <MyInvestmentExchangeButton
                id={investment.id}
                companyId={companyId}
                onClickRequest={onClickRequest}
                userCompanySEStatus={userCompanySEStatus}
                companyActiveShareExchange={companyActiveShareExchange}
                declineReason={declineReason}
                serDeclined={serDeclined}
                isActiveExchange={isActiveExchange}
              />
            )}
          </TableData>
        )}
        <TableData>
          <Tooltip
            anchorId={tooltipAnchorId}
            isOpen={isOpen}
            positionStrategy='fixed'
            className='text-sm text-grey-500 z-10 !mt-0'
            offset={0}
            clickable
            noArrow
          >
            {companyName || investmentName}
          </Tooltip>
          <div className='reduced-view' id={tooltipAnchorId} onMouseEnter={onOpen} onMouseLeave={onClose}>
            <MyInvestmentsNameTableDataContent
              isFirstElement={isFirstElement}
              investmentName={investmentName}
              companyName={companyName || ''}
              companyId={companyId}
              isDaxia={isDaxiaInvestment}
            />
          </div>
        </TableData>
        <TableData>{isDaxiaInvestment ? investmentCompanyNumber : companyNumber}</TableData>
        <TableData className='min-w-[100px]'>{investment.classOfShares}</TableData>
        <TableDataWithTooltip
          anchorId={`${investment.id}-pricePerShare`}
          tooltipContent={INVESTMENTS_DAXIA_PRICE_PER_SHARE_TOOLTIP_CONTENT}
          hidden={!isDaxiaInvestment || !isBlended}
          className='min-w-[175px]'
        >
          £{formatNumberInputValue(investment.pricePerShare, undefined, true)}
        </TableDataWithTooltip>
        <TableData>
          {status !== InvestmentStatuses.EXCHANGED
            ? `£${formatNumberInputValue(currentInvestmentValue, undefined, true)}`
            : '-'}
        </TableData>
        <TableData>{investment.investmentDate ? investment.investmentDate : '-'}</TableData>
        <TableData>{totalShares !== 0 ? formatAsThousands(totalShares) : '-'}</TableData>
        <TableData>
          {originalInvestmentPrice && `£${formatNumberInputValue(originalInvestmentPrice, undefined, true)}`}
        </TableData>
        <TableData className='min-w-[100px]'>
          {status && <ColoredLabel {...investmentStatusesColoredLabel[status]} />}
        </TableData>
        {setEditId && (
          <TableData>
            {!isDaxiaInvestment && (
              <ButtonsActions anchorPrefix={investment.id.toString()} actions={getInvestmentButtonsActions()} />
            )}
          </TableData>
        )}
      </TableRow>

      {setEditId && (
        <InvestmentsTableFormRow
          colSpan={10}
          isShown={!!isEditing}
          investment={investmentToEdit}
          onCancel={() => setEditId?.(null)}
          onSuccessEdit={onSuccessEditInvestment}
          isEditMode
        />
      )}
    </>
  );
};

export default MyCompaniesWithInvestmentsTableItem;
